import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Header from "../../components/ui/Header";
import Breadcrumb from "../../components/ui/Breadcrumb";
import ImageGallery from "./components/ImageGallery";
import ApartmentInfo from "./components/ApartmentInfo";
import ContactForm from "./components/ContactForm";
import BookingPanel from "./components/BookingPanel";
import RelatedApartments from "./components/RelatedApartments";
import Icon from "../../components/AppIcon";

import { APARTMENTS } from "../../data/properties";
import {
  PRICING_BY_SLUG,
  getPriceForDate,
  buildSeasonalPricesByMonth,
} from "../../data/pricing";
import { Helmet } from "react-helmet-async";

const getTzDateParts = (timeZone = "Europe/Zagreb") => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(new Date());
  const get = (t) => Number(parts.find((p) => p.type === t)?.value);
  return { y: get("year"), m: get("month"), d: get("day") };
};

export default function ApartmentDetailSlug() {
  const { slug } = useParams();
  const [apartment, setApartment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDates, setSelectedDates] = useState({
    checkin: null,
    checkout: null,
  });

  const base = useMemo(() => APARTMENTS.find((a) => a.slug === slug), [slug]);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!base) {
        setApartment(null);
        setLoading(false);
        return;
      }

      const { y, m, d } = getTzDateParts("Europe/Zagreb");
      const today = new Date(Date.UTC(y, m - 1, d));
      const seasons = PRICING_BY_SLUG[base.slug] || [];
      const todays = getPriceForDate(seasons, today, 2) ?? 0;

      setApartment({
        ...base,
        price: todays,
        seasons,
        seasonalPrices: buildSeasonalPricesByMonth(seasons),
      });

      setLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [base]);
  const [currentPrice, setCurrentPrice] = useState(null);

  const handlePriceChange = (priceOrNull) => {
    if (priceOrNull == null) {
      setCurrentPrice(null);
      return;
    }
    setCurrentPrice(priceOrNull);
  };

  const breadcrumbItems = [
    { label: "Home", path: "/", icon: "Home" },
    { label: "Apartments", path: "/apartmani", icon: "Building" },
    {
      label: apartment?.title || "Apartment Details",
      isActive: true,
      icon: "MapPin",
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-6 lg:px-8 py-12">
          <div className="animate-pulse">
            <div className="h-96 bg-muted rounded-xl mb-8"></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-6">
                <div className="h-8 bg-muted rounded w-3/4"></div>
                <div className="h-4 bg-muted rounded w-full"></div>
                <div className="h-4 bg-muted rounded w-2/3"></div>
              </div>
              <div className="h-64 bg-muted rounded-xl"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!apartment) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon
            name="AlertTriangle"
            size={48}
            className="text-muted-foreground mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-foreground mb-2">
            Apartment Not Found
          </h1>
          <p className="text-muted-foreground">
            The apartment you're looking for doesn't exist.
          </p>
        </div>
      </div>
    );
  }

  const { y, m, d } = getTzDateParts("Europe/Zagreb");
  const today = new Date(Date.UTC(y, m - 1, d));

  const relatedApartments = APARTMENTS.filter(
    (a) => a.slug !== apartment.slug
  ).map((a) => {
    const seasons = PRICING_BY_SLUG[a.slug] || [];

    return {
      id: a.slug,
      slug: a.slug,
      title: a.title,
      location: "Guest House Sonja",
      imageUrl: a.images?.[0],
      link: `/apartmani/${a.slug}`,
      status: a.status,
      capacity: a.capacity,
      seasons,
      seasonalPrices: buildSeasonalPricesByMonth(seasons),
      price: getPriceForDate(seasons, today, 2) ?? 0,
    };
  });

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{apartment.title} | Guest House Sonja Krk</title>
        <meta
          name="description"
          content={`Stay at ${apartment.title} in Guest House Sonja Krk. Modern apartment near the center of Krk with free WiFi and parking.`}
        />
        <link
          rel="canonical"
          href={`https://www.guesthousesonjakrk.com/apartmani/${apartment.slug}`}
        />
      </Helmet>
      <Header />
      <div className="container mx-auto px-6 lg:px-8 py-6">
        <Breadcrumb customItems={breadcrumbItems} />
        <ImageGallery images={apartment.images} title={apartment.title} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
          <div className="lg:col-span-2">
            <ApartmentInfo
              apartment={apartment}
              starCount={4}
              currentPrice={currentPrice}
            />
            <div className="mt-12">
              <ContactForm apartment={apartment} prefillDates={selectedDates} />
            </div>
          </div>

          <div className="lg:col-span-1">
            <BookingPanel
              apartment={apartment}
              onPriceChange={handlePriceChange}
              onDatesChange={setSelectedDates}
            />
          </div>
        </div>

        <div className="mt-16">
          <RelatedApartments
            apartments={relatedApartments}
            viewAllPath="/apartmani"
          />
        </div>
      </div>
    </div>
  );
}
